import React, { useState } from 'react';
import {
  Container,
  Header,
  Form,
  FormField,
  Input,
  Textarea,
  SpaceBetween,
  Button,
  Box,
  Alert,
  RadioGroup,
  StatusIndicator,
  ColumnLayout
} from '@amzn/awsui-components-console';

export interface TableInfo {
  name: string;
  owner: string;
  classification: string;
}

export interface DynamicQuestion {
  id: string;
  question: string;
  type: 'text' | 'textarea' | 'radio' | 'select';
  required: boolean;
  options?: Array<{ label: string; value: string }>;
  placeholder?: string;
}

export interface FormPanelProps {
  title: string;
  tables: TableInfo[];
  consumer?: string;
  teamName?: string;
  businessJustification?: string;
  additionalInformation?: string;
  dynamicQuestions?: DynamicQuestion[];
  state?: 'loading' | 'editing' | 'validating' | 'submitting' | 'submitted';
  validationErrors?: Record<string, string>;
  requestId?: string;
  onSubmit?: (formData: Record<string, any>) => void;
  onCancel?: () => void;
}

/**
 * FormPanel - Inline form for collecting structured input
 * 
 * Displays a multi-step form for access requests with:
 * - Read-only table information
 * - Auto-populated consumer (IAM role)
 * - Required fields (team name, business justification)
 * - Optional additional information
 * - Dynamic questions from table owner
 * - Submit and cancel actions
 * 
 * When to use: Fintella access requests, Andes access requests
 */
export const FormPanel: React.FC<FormPanelProps> = ({
  title,
  tables,
  consumer,
  teamName: initialTeamName = '',
  businessJustification: initialBusinessJustification = '',
  additionalInformation: initialAdditionalInformation = '',
  dynamicQuestions = [],
  state = 'editing',
  validationErrors = {},
  requestId,
  onSubmit,
  onCancel
}) => {
  const [teamName, setTeamName] = useState(initialTeamName);
  const [businessJustification, setBusinessJustification] = useState(initialBusinessJustification);
  const [additionalInformation, setAdditionalInformation] = useState(initialAdditionalInformation);
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});

  const handleQuestionChange = (questionId: string, value: string) => {
    setQuestionAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit({
        teamName,
        businessJustification,
        additionalInformation,
        questionAnswers
      });
    }
  };

  // Loading state - fetching questions from Fintella
  if (state === 'loading') {
    return (
      <Container
        header={<Header variant="h3">{title}</Header>}
      >
        <Box padding={{ vertical: 'l' }} textAlign="center">
          <StatusIndicator type="loading">
            Loading form requirements...
          </StatusIndicator>
        </Box>
      </Container>
    );
  }

  // Submitted state - show success notification
  if (state === 'submitted' && requestId) {
    return (
      <Container>
        <Alert
          type="success"
          header="Access request submitted successfully"
        >
          Your request has been submitted with ID: <strong>{requestId}</strong>
          <Box padding={{ top: 's' }}>
            You will receive a notification when the request is processed.
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      header={<Header variant="h3">{title}</Header>}
    >
      <form onSubmit={handleSubmit}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={onCancel}
                disabled={state === 'submitting'}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={state === 'submitting'}
                disabled={state === 'submitting'}
              >
                Submit Request
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            {/* Tables Section - Read Only */}
            <FormField
              label="Tables"
              description="Requesting READ access on behalf of ADDAPT"
            >
              <Box>
                {tables.map((table, index) => (
                  <Box
                    key={index}
                    padding="s"
                    margin={{ bottom: 'xs' }}
                    variant="div"
                  >
                    <div style={{
                      border: '1px solid #e9ebed',
                      borderRadius: '8px',
                      padding: '12px',
                      backgroundColor: '#fafafa'
                    }}>
                      <Box fontSize="body-m" fontWeight="bold">
                        {table.name}
                      </Box>
                      <ColumnLayout columns={2} variant="text-grid">
                        <div>
                          <Box variant="awsui-key-label">Owner</Box>
                          <div>{table.owner}</div>
                        </div>
                        <div>
                          <Box variant="awsui-key-label">Classification</Box>
                          <div>{table.classification}</div>
                        </div>
                      </ColumnLayout>
                    </div>
                  </Box>
                ))}
              </Box>
            </FormField>

            {/* Consumer - Auto-populated */}
            {consumer && (
              <FormField
                label="Consumer (IAM Role)"
                description="Auto-populated from ADDAPT service role"
              >
                <Input
                  value={consumer}
                  disabled
                  readOnly
                />
              </FormField>
            )}

            {/* Team Name - Required */}
            <FormField
              label="Team Name"
              constraintText="Required"
              errorText={validationErrors.teamName}
            >
              <Input
                value={teamName}
                onChange={({ detail }) => setTeamName(detail.value)}
                placeholder="Enter team name"
                disabled={state === 'submitting'}
                invalid={!!validationErrors.teamName}
              />
            </FormField>

            {/* Business Justification - Required */}
            <FormField
              label="Business Justification"
              constraintText="Required"
              errorText={validationErrors.businessJustification}
            >
              <Textarea
                value={businessJustification}
                onChange={({ detail }) => setBusinessJustification(detail.value)}
                placeholder="Explain why you need access to these tables"
                rows={3}
                disabled={state === 'submitting'}
                invalid={!!validationErrors.businessJustification}
              />
            </FormField>

            {/* Additional Information - Optional */}
            <FormField
              label="Additional Information"
              description="Optional"
            >
              <Textarea
                value={additionalInformation}
                onChange={({ detail }) => setAdditionalInformation(detail.value)}
                placeholder="Any additional context or information"
                rows={2}
                disabled={state === 'submitting'}
              />
            </FormField>

            {/* Dynamic Questions from Table Owner */}
            {dynamicQuestions.length > 0 && (
              <>
                <Box
                  padding={{ top: 's', bottom: 's' }}
                  fontSize="heading-s"
                  fontWeight="bold"
                >
                  Required Questions (from table owner)
                </Box>

                {dynamicQuestions.map((question) => (
                  <FormField
                    key={question.id}
                    label={question.question}
                    constraintText={question.required ? 'Required' : 'Optional'}
                    errorText={validationErrors[question.id]}
                  >
                    {question.type === 'text' && (
                      <Input
                        value={questionAnswers[question.id] || ''}
                        onChange={({ detail }) => handleQuestionChange(question.id, detail.value)}
                        placeholder={question.placeholder || 'Enter your answer'}
                        disabled={state === 'submitting'}
                        invalid={!!validationErrors[question.id]}
                      />
                    )}
                    {question.type === 'textarea' && (
                      <Textarea
                        value={questionAnswers[question.id] || ''}
                        onChange={({ detail }) => handleQuestionChange(question.id, detail.value)}
                        placeholder={question.placeholder || 'Enter your answer'}
                        rows={3}
                        disabled={state === 'submitting'}
                        invalid={!!validationErrors[question.id]}
                      />
                    )}
                    {question.type === 'radio' && question.options && (
                      <RadioGroup
                        value={questionAnswers[question.id] || ''}
                        onChange={({ detail }) => handleQuestionChange(question.id, detail.value)}
                        items={question.options}
                      />
                    )}
                  </FormField>
                ))}
              </>
            )}

            {/* Validation Errors Alert */}
            {state === 'validating' && Object.keys(validationErrors).length > 0 && (
              <Alert type="error" header="Please fix the following errors">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {Object.entries(validationErrors).map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </SpaceBetween>
        </Form>
      </form>
    </Container>
  );
};

export default FormPanel;
